import '../components/widgets/RydViewport.css';

import React, {
  useEffect,
} from 'react';
import {
  PianoRoll,
  PianoRollNoteSet
} from '../components/rolls/PianoRoll';
import {
  ChordRoll,
  ChordSelectionDialog,
  ToneRollKeybed
} from '../components/rolls/ChordRoll';
import {
  convertCtxToComponentModel,
  testData,
} from '../testData.js';
import _ from 'lodash';
import ChordRecognizer from '../components/ChordRecognizer.js';
import DemoData from '../utils/DemoData';
import {
  RydViewport,
  RydViewportSticky
} from '../components/widgets/RydViewport';
import NoteDurationSelector from '../components/pickers/DurationSelector';
import NoteRangeSelector, {
  FULL_RANGE
} from '../components/pickers/NoteRangeSelector';
import TimeSignatureInputBox from '../components/ContextTimeSignature';
import {
  TapTempoButton,
  MetronomeInputBox
} from '../components/pickers/ContextMetronome';
import {
  Chord,
  NoteDuration,
  Tone,
  MetronomeMarking,
  TimeSignature
} from '../model/core';
import {
  IntervalPattern,
  ScalePattern,
  Tonality,
} from '../model/scales';
import '../testbeds/EurydiceTestbed.css';
import {
  init,
  listInputs,
  listOutputs,
  startLoggingMIDIInput,
  stopLoggingMIDIInput
} from '../audio/MidiController';
import {
  ChordContainer,
  HorizontalNoteContainer,
  NoteRangeType
} from '../model/containers';
import { TonalityInputBox } from '../components/pickers/ContextTonality';

import { wrap } from './RydTestbedWrap';
import { RydFloatingPanel } from '../components/widgets/RydFloatingPanel';
import { MidiDevicePanel } from '../components/midi/MidiDevicePanel';

import { MidiDeviceSelectionDialog } from '../components/midi/MidiDeviceSelectionDialog';
import {
  AudioSink,
  listAudioSinks
} from '../audio/routing';
import {
  ContextPlaneOld,
  RydContextLayerPlane
} from '../components/contextPlane/RydContextLayer';
import {
  PianoRollKeybed,
  RydIOKeybed
} from '../components/rolls/PianoKeybed';
import PianoRollOperationContainer from '../components/rolls/PianoRollOperationContainer';
import {
  ComputedContextFrame,
} from '../model/context';
import { TrackColor } from '../render/trackColor';
import { PianoRollInfoHeader } from '../components/rolls/PianoRollInfoHeader';
import {
  RydTrack,
  RydTrackUIState
} from '../model/tracks';
import { RydProject } from '../model/project';


const FOUR_FOUR = new TimeSignature(4, 4);
export const CONTEXT: ComputedContextFrame = {
  timeSignature: FOUR_FOUR,
  metronome: new MetronomeMarking(120, NoteDuration.fromTimeSignature(FOUR_FOUR.denominator)),
  tonality: new Tonality(Tone.fromName('C'), new ScalePattern(new IntervalPattern('MAJOR', [2, 2, 1, 2, 2, 2, 1])))
};

const TRACK : RydTrack = {
  id: 1,
  name: 'Demo Track',
  length: 16,
  color: TrackColor.getRandomColor(),
  sequence: DemoData.courante(16) as HorizontalNoteContainer,
  bookmarks: [],
};

const DATA = {

  pianoRollKeybed: (<>
    <h2>PianoRollKeybed</h2>
    <h3>Horizontal</h3>
    <PianoRollKeybed noteRange={FULL_RANGE} orientation={'keyboard'} />
    <h3>Full range</h3>
    <PianoRollKeybed noteRange={FULL_RANGE} />
    <h3>Partial range</h3>
    <PianoRollKeybed noteRange={{ min: 50, max: 74 }} />
    <h3>Tones only</h3>
    <ToneRollKeybed />
    <h2>IO component</h2>
    <h3>Defaults</h3>
    <RydIOKeybed />
    <h3>note range</h3>
    <RydIOKeybed noteRange={{ min: 40, max: 60 }} />
  </>),

  chordRoll: (<>
    <h2>ChordRoll</h2>
    <h3>Collapsed</h3>
    <ChordRoll
      line={DemoData.weihnachtsBaeckerei().get(0) as ChordContainer}
      trackId={1}
      contextFrame={CONTEXT}
      trackUiState={{
        trackId: 1,
        collapsed: true,
        noteRange: FULL_RANGE,
        tonalityVisible: false,
        color: TrackColor.DEFAULT_COLOR,
      }}
      updateLineCB={() => {}}
    />
    <h3>Harmonic Field</h3>
    <ChordRoll
      trackId={1}
      line={DemoData.harmonicFieldChordLine()}
      contextFrame={CONTEXT}
      trackUiState={{
        trackId: 1,
        collapsed: true,
        noteRange: FULL_RANGE,
        tonalityVisible: false,
        color: TrackColor.DEFAULT_COLOR,
      }}
      updateLineCB={() => {}}
    />
    <h3>Blank</h3>
    <ChordRoll
      trackId={1}
      line={DemoData.emptyChordContainer(8)}
      contextFrame={CONTEXT}
      trackUiState={{
        trackId: 1,
        collapsed: true,
        noteRange: FULL_RANGE,
        tonalityVisible: false,
        color: TrackColor.DEFAULT_COLOR,
      }}
      updateLineCB={() => {}}
    />
  </>),

  chordRecognizer: (
    <>
      <h2>Chord recognizer</h2>
      <ChordRecognizer />
    </>
  ),

  contextPlane: (<>
    <h2>Context plane</h2>
    <ContextPlaneOld data={convertCtxToComponentModel(testData)} />
    <RydContextLayerPlane />
    <RydContextLayerPlane cp={DemoData.circleOfFifths()}/>
  </>),

};

function EurydicePianoRollKeybed() {
  return wrap(DATA.pianoRollKeybed);
}

function EurydiceContextPlane() {
  return wrap(DATA.contextPlane);
}

function EurydiceChordRoll() {
  return wrap(DATA.chordRoll);
}

function EurydiceChordRecognizer() {
  return wrap(DATA.chordRecognizer);
}

function EurydicePROperationContainer() {
  return wrap(
    <><h3>Piano roll operation</h3>
      <PianoRollOperationContainer readonly={false} />
    </>);
}

function EurydicePianoRollViewport() {

  const dummyText = (<ol>{_.range(0, 100).map((i) => <li key={i} style={{ textWrap: 'nowrap' }}>{DemoData.lipsum(100)}</li>)}</ol>);

  const prLength = 100;
  const noteRange = { min: 10, max: 70 };
  const COURANTE = DemoData.courante(prLength);

  const header = <PianoRollInfoHeader
    hnc={COURANTE}
    track={TRACK}
    setTrack={(t) => console.log('setTrack', t)}
    noteRange={{ min: 50, max: 70 }}
    readonly={false}
    toggleSheetVisibilityCB={() => console.log('toggleSheetVisibilityCB')}
    tonalityHighlightVisible={false}
    toggleTonalityHighlight={() => console.log('toggleTonalityHighlight')}
    collapsed={false}
    setCollapsed={(c) => console.log('toggleCollapsedCB')}
    updateNoteRangeCB={() => console.log('updateNoteRangeCB')}
    trackColor='#ff0000' // Example color
    setTrackColor={(color) => console.log('setTrackColor', color)}
  />;

  return wrap(<>
    <table>
      <tr><td></td></tr>
      <tr><td>
        <RydViewport key={1} name="uno" />
      </td></tr>
      <tr><td>
        <RydViewport key={2} name="dos">
          {dummyText}
        </RydViewport>
      </td></tr>
      <tr><td>
        <RydViewport key={3} name="três" sticky={<RydViewportSticky />} />
      </td></tr>
      <tr><td>
        <RydViewport key={4} name="piat" sticky={<RydViewportSticky />}>
          {dummyText}
        </RydViewport>
      </td></tr>
      <tr><td>
        <RydViewport name="fünf">
          {header}
        </RydViewport>
      </td></tr>
      <tr><td>
        <RydViewport name="666" sticky={<RydViewportSticky />} header={header}>
          {dummyText}
        </RydViewport>
      </td></tr>
      <tr><td>
        <RydViewport name="vii" sticky={<RydViewportSticky />} header={header}>
          <PianoRollKeybed noteRange={{ min: 50, max: 70 }} />
        </RydViewport>
      </td></tr>
      <tr><td>
        <RydViewport name="infinity 1" header={header}>
          <div className="piano-roll-content-wrapper">

            <PianoRollKeybed noteRange={noteRange} />

            {/* {_.range(0, prLength).map((i) =>
              <PianoRollTick
                key={i}
                start={i}
                // updatePianoRollCB={_.curry(updatePianoRoll)(config.readonly)(i)}
                readonly={false}
                noteRange={noteRange} />)} */}
            <PianoRollNoteSet
              trackId={1}
              readonly={false}
              updateHNCCB={() => console.log('updateHNCCB')}
              hnc={COURANTE}
              noteRange={noteRange}
              />
          </div>

        </RydViewport>
      </td></tr>
    </table>
  </>);
}

function EurydiceDurationSelector() {

  const [duration, setDuration] = React.useState<NoteDuration>();
  const [noteRange, setNoteRange] = React.useState<NoteRangeType>();

  const el = (<>
    <h1>Duration selector</h1>
    Selected: {duration?.value}
    <NoteDurationSelector updateSelectedDuratonCB={setDuration} />

    <h1>Note range selector</h1>
    Selected: {noteRange?.min} - {noteRange?.max}
    <NoteRangeSelector updateNoteRangeCB={setNoteRange} noteRange={{min: 0, max: 10}} />
  </>);

  return wrap(el);
}

function EurydiceContextTimeSignature() {
  const [timeSignature, setTimeSignature] = React.useState<TimeSignature>();
  const [tapTempo, setTapTempo] = React.useState<number>(0);

  const el = (<><h2>Time signature</h2>
    {/* <h3>No props</h3>
    <TimeSignatureInputBox timeSignature={}/> */}
    <h3>Props: init with 7/8</h3>
    <TimeSignatureInputBox timeSignature={{numerator: 7, denominator: 8}}/>
    <h3>Props: with callback</h3>
    Selected {timeSignature?.numerator}/{timeSignature?.denominator}
    <TimeSignatureInputBox timeSignature={{numerator: 7, denominator: 8}} setTimeSignatureCB={setTimeSignature}/>

    <h2>Metronome</h2>
    <h3>No props</h3>
    <MetronomeInputBox  />
    <h3>Props: init with 92 bpm</h3>
    <MetronomeInputBox metronome={new MetronomeMarking(92, new NoteDuration(16))} />

    <h3>tap tempo</h3>
    <TapTempoButton setTempoCB={setTapTempo}/>
    Tempo: {tapTempo} bpm

  </>)
  return wrap(el);
}

function EurydiceCollpasedRolls() {
  const [chord, setChord] = React.useState('foo');
  const [chordSelectorRefEl, setChordSelectorRefEl] = React.useState<EventTarget & HTMLElement>();
  const [uiState, setUiState] = React.useState<RydTrackUIState>({
    trackId: 1,
    collapsed: true,
    noteRange: FULL_RANGE,
    tonalityVisible: false,
    color: TrackColor.DEFAULT_COLOR,
  });

  return wrap(
    <>
      <h1>Collapsed rolls</h1>
      <h2>Chord input collapsed</h2>
      <h3>empty</h3>
      Selected chord: {chord}
      <ChordSelectionDialog
        acceptCB={(c) => console.log('accept')}
        discardCB={() => console.log('discard')}
        removeCB={() => console.log('remove')} />
      <h3>pre init with C dim</h3>
      <ChordSelectionDialog
        acceptCB={(c) => console.log('accept')}
        chord={new Chord(Tone.fromName('C'), 'dim')}
        discardCB={() => console.log('discard')}
        removeCB={() => console.log('remove')}
        />
      <h3>floating</h3>

      <button onClick={(e) => setChordSelectorRefEl(e.currentTarget)}>reference</button>
      {chordSelectorRefEl &&
        <RydFloatingPanel referenceElement={chordSelectorRefEl} offsetX={10} offsetY={10}>
          <ChordSelectionDialog
            acceptCB={(c) => console.log('accept')}
            discardCB={() => console.log('discard')}
            removeCB={() => console.log('remove')} />
        </RydFloatingPanel>}
      <h2>Chord roll</h2>

      <ChordRoll
        line={DemoData.harmonicFieldChordLine()}
        trackId={1}
        contextFrame={CONTEXT}
        trackUiState={{
          trackId: 1,
          collapsed: true,
          noteRange: FULL_RANGE,
          tonalityVisible: false,
          color: TrackColor.DEFAULT_COLOR,
        }}
        updateLineCB={() => {}}
      />

      <h2>Piano roll</h2>
      <PianoRoll
        project={RydProject.createForNewProject('Test Project')}
        hnc={DemoData.courante(100)}
        track={TRACK}
        setTrack={(t) => console.log('setTrack', t)}
        contextFrame={CONTEXT}
        readonly={false}
        uiState={uiState}
        setTrackUIState={(uiState) => {
          console.log('setTrackUIState', uiState);
          setUiState({...uiState});
        }}
      />

    </>
  );
}

function onMIDIMessage(event) {
  let str = `MIDI message received at timestamp ${event.timeStamp}[${event.data.length} bytes]: `;
  for (const character of event.data) {
    str += `0x${character.toString(16)} `;
  }
  console.log(str);
}

const stopSoundingNote = (midiAccess, inputId) => {
  const output = midiAccess.outputs.get(inputId);
  const data = new Uint8Array([0x80, 60, 0]); // note off, middle C
  output.send(data);
}

function EurydiceMidiTestbed() {

  const [midiAccess, setMIDIAccess] = React.useState<any>(null);
  const [sequence, setSequence] = React.useState<HorizontalNoteContainer>(DemoData.empty(16));
  const [step, setStep] = React.useState<number>(0);
  const [playing, setPlaying] = React.useState<boolean>(false);
  const [lastNote, setLastNote] = React.useState<number | undefined>(0);

  useEffect(() => {
    if(!playing) {
      return;
    }
    const interval = setInterval(() => {
      console.log('playing', step, sequence.get(step));
      if (!midiAccess || !midiAccess.outputs) {
        console.error('No MIDI outputs available');
        return;
      }
      const output = midiAccess.outputs.get('DA5260506C656C83470B2A6DF3AFC970FBC9F0FB081C9C74D5947A4A156771EA');
      if (lastNote) {
        const stop = new Uint8Array([0x80, lastNote, 0]); // note off, middle C
        output.send(stop);
      }
      setStep((step + 1) % sequence.getLength());
      if (sequence.get(step).length > 0) {
        const data = new Uint8Array([0x90, sequence.get(step)[0].payload.index, 100]); // note on, middle C, full velocity
        output.send(data);
        setLastNote(sequence.get(step)[0].payload.index)
      } else {
        setLastNote(undefined);
      }
    }, 250);
    return () => clearInterval(interval);
  }, [playing, step, sequence]);

  const playNote = (midiAccess, inputId, isPlaying) => {
    //console.log('midi access play', midiAccess, 'input id', inputId);
    if (!midiAccess || !midiAccess.outputs) {
      console.error('No MIDI outputs available');
      return;
    }
    console.log(sequence);
    if (!sequence || sequence?.getLength() === 0) {
      console.error('No sequence available');
      return;
    }

    const output = midiAccess.outputs.get(inputId);
    const stop = new Uint8Array([0x80, 60, 0]); // note off, middle C
    output.send(stop);

    if (isPlaying) {
      console.log('scheduling...');
      console.log('playing', step, sequence.get(step));
      const data = new Uint8Array([0x90, 60, 100]); // note on, middle C, full velocity
      output.send(data);
      //setTimeout(playNote, 250, midiAccess, inputId, playing);
      //setInterval(playNote, 250, midiAccess, inputId, playing);
      setStep(step + 1 % sequence.getLength());
    }
  };

  const startPlayback = (midiAccess, { inputId }) => {
    console.log('midi access start', midiAccess, 'input id', inputId);
    if (!midiAccess || !midiAccess.outputs) {
      console.error('No MIDI outputs available');
      return;
    }

    setPlaying(true);
    //playNote(midiAccess, inputId, true);

  }

  const stopPlayback = (midiAccess, { inputId }) => {
    console.log('midi access stop', midiAccess, 'input id', inputId);
    if (!midiAccess || !midiAccess.outputs) {
      console.error('No MIDI outputs available');
      return;
    }

    setPlaying(false);

  }

  return wrap(
    <>
      <h1>Midi testbed</h1>
      <h2>Access</h2>
      Midi access: {midiAccess ? 'granted' : 'denied'}
      {!midiAccess &&
        <button onClick={() => init(setMIDIAccess)}>midi permissions</button>
      }
      <h2>Controls</h2>
      <h3>Capture</h3>
      <ol>
        {midiAccess && listInputs(midiAccess).map((io) => <li key={io.id}>{io.id}: {io.name}

          <button onClick={() => startLoggingMIDIInput(midiAccess, { inputId: io.id }, onMIDIMessage)}>Start capture</button>
          <button onClick={() => stopLoggingMIDIInput(midiAccess, { inputId: io.id })}>stop capture</button>

        </li>)}
      </ol>
      <ol>
        {midiAccess && listOutputs(midiAccess).map((io) => <li key={io.id}>{io.id}: {io.name}
        <button onClick={() => startPlayback(midiAccess, { inputId: io.id })}>Start playback</button>
        <button onClick={() => stopPlayback(midiAccess, { inputId: io.id })}>Stop playback</button>
        </li>)}
      </ol>

      <h3>Selector panel</h3>
      <MidiDevicePanel />

      <h3>Header selector</h3>
      {!midiAccess &&
        <button onClick={() => init(setMIDIAccess)}>midi permissions</button>
      }

      <MidiDeviceSelectionDialog<AudioSink>
        listSource={listAudioSinks}
        setCB={(s) => { }} />

      <h3>Playback</h3>
      Step {step + 1}/{sequence?.getLength()} {playing ? 'playing' : 'stopped'}
      <PianoRoll
        project={RydProject.createForNewProject('Test Project')}
        track={TRACK}
        setTrack={(t) => console.log('setTrack', t)}
        hnc={sequence}
        contextFrame={CONTEXT}
        readonly={false}
        setTrackUIState={(uiState) => console.log('setTrackUIState', uiState)}
      />
    </>
  );
}

function EurydiceTonalitySelector() {
  const t: Tonality = new Tonality(Tone.fromName('F#'), new ScalePattern(new IntervalPattern('LOCRIAN', [2, 2, 1, 2, 2, 2, 1])));

  return wrap(<>
    <h2>Tonality Selector</h2>
    <TonalityInputBox />
    <TonalityInputBox tonality={t} setTonalityCB={(t) => console.log(t)}/>

  </>);
}

function EurydiceHighlightColors() {

  const backgroundColor = 'var(--vi-p-12)';

  return wrap(<>
    <h2>Highligh colors</h2>
    <div style={{ display: 'flow', flexDirection: 'row', padding: '3em', backgroundColor: backgroundColor }}>
      <div >
        <div style={{ display: 'inline-block', width: '30px', backgroundColor: 'var(--pr-kb-body-white)' }}>A1</div>
        <div style={{ display: 'inline-block', width: '30px', backgroundColor: 'var(--pr-kb-body-white)', opacity: 0.5 }}>B1</div>
        <div style={{ display: 'inline-block', width: '30px', backgroundColor: 'white' }}>C1</div>
        <div style={{ display: 'inline-block', width: '30px', backgroundColor: 'white' }}>D1</div>
      </div>
      <div >
        <div style={{ display: 'inline-block', width: '30px', backgroundColor: 'var(--pr-kb-body-black)' }}>A2</div>
        <div style={{ display: 'inline-block', width: '30px', backgroundColor: 'var(--pr-kb-body-black)', opacity: 0.5 }}>B2</div>
        <div style={{ display: 'inline-block', width: '30px', backgroundColor: 'white' }}>C2</div>
        <div style={{ display: 'inline-block', width: '30px', backgroundColor: 'white' }}>D2</div>
      </div>
      <div >
        <div style={{ display: 'inline-block', width: '30px', backgroundColor: 'var(--pr-kb-body-white)' }}>A2</div>
        <div style={{ display: 'inline-block', width: '30px', backgroundColor: 'var(--pr-kb-body-white)', opacity: 0.5 }}>B2</div>
        <div style={{ display: 'inline-block', width: '30px', backgroundColor: 'white' }}>C2</div>
        <div style={{ display: 'inline-block', width: '30px', backgroundColor: 'white' }}>D2</div>
      </div>
    </div>
  </>);
}

function EurydiceComponentsCollection() {
  return wrap(_.keys(DATA).map((k) => DATA[k]));
}

export {
  EurydiceContextPlane,
  EurydiceComponentsCollection,
  EurydiceChordRoll,
  EurydiceChordRecognizer,
  EurydicePianoRollKeybed,
  EurydicePROperationContainer,
  EurydicePianoRollViewport,
  EurydiceDurationSelector,
  EurydiceContextTimeSignature,
  EurydiceCollpasedRolls,
  EurydiceMidiTestbed,
  EurydiceTonalitySelector,
  EurydiceHighlightColors
};